import { useState, useCallback, useMemo } from 'react';
import type { ItemKind } from '../../types';

const useFolderSelection = (closeOnMobile: () => void) => {
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [selectedPromptId, setSelectedPromptId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [selectedSpecialView, setSelectedSpecialView] = useState<string | null>('all');
  const [isTemplateManagerOpen, setIsTemplateManagerOpen] = useState(false);
  const [isPromptGeneratorOpen, setIsPromptGeneratorOpen] = useState(false);
  const [selectedPromptIds, setSelectedPromptIds] = useState<Set<string>>(new Set());
  const [kindFilter, setKindFilter] = useState<ItemKind | 'all'>('all');

  const handleSelection = useCallback((cb: () => void) => {
    cb();
    setSelectedPromptIds(new Set());
    closeOnMobile();
  }, [closeOnMobile]);

  const handleSelectPrompt = useCallback((id: string | null) => {
    setSelectedPromptId(id);
    if (id !== null) {
        setSelectedPromptIds(new Set());
    }
  }, []);

  const toggleSelectPrompt = useCallback((id: string) => {
    setSelectedPromptIds(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
    });
    setSelectedPromptId(null);
  }, []);

  const clearPromptSelection = useCallback(() => setSelectedPromptIds(new Set()), []);

  const handleSelectFolder = useCallback((id: string | null) => handleSelection(() => {
    setSelectedFolderId(id);
    setActiveTag(null);
    setSelectedPromptId(null);
    setSelectedSpecialView(id === null ? 'all' : null);
    setIsTemplateManagerOpen(false);
    setIsPromptGeneratorOpen(false);
  }), [handleSelection]);

  const handleSelectTag = useCallback((tag: string | null) => handleSelection(() => {
    setActiveTag(tag);
    setSelectedFolderId(null);
    setSelectedPromptId(null);
    setSelectedSpecialView(tag ? 'tag' : 'all');
    setIsTemplateManagerOpen(false);
    setIsPromptGeneratorOpen(false);
  }), [handleSelection]);

  const handleShowRecent = useCallback(() => handleSelection(() => {
     setSelectedFolderId(null);
     setActiveTag(null);
     setSelectedPromptId(null);
     setSelectedSpecialView('recent');
     setIsTemplateManagerOpen(false);
     setIsPromptGeneratorOpen(false);
  }), [handleSelection]);

  const handleShowFavorites = useCallback(() => handleSelection(() => {
    setSelectedFolderId(null);
    setActiveTag(null);
    setSelectedPromptId(null);
    setSelectedSpecialView('favorites');
    setIsTemplateManagerOpen(false);
    setIsPromptGeneratorOpen(false);
  }), [handleSelection]);

  const handleShowTrash = useCallback(() => handleSelection(() => {
    setSelectedFolderId(null);
    setActiveTag(null);
    setSelectedPromptId(null);
    setSelectedSpecialView('trash');
    setIsTemplateManagerOpen(false);
    setIsPromptGeneratorOpen(false);
  }), [handleSelection]);

  const handleOpenTemplateManager = useCallback(() => handleSelection(() => {
    setSelectedFolderId(null);
    setActiveTag(null);
    setSelectedPromptId(null);
    setSelectedSpecialView('templates');
    setIsTemplateManagerOpen(true);
    setIsPromptGeneratorOpen(false);
  }), [handleSelection]);

  const handleOpenPromptGenerator = useCallback(() => handleSelection(() => {
    setSelectedFolderId(null);
    setActiveTag(null);
    setSelectedPromptId(null);
    setSelectedSpecialView('generator');
    setIsTemplateManagerOpen(false);
    setIsPromptGeneratorOpen(true);
  }), [handleSelection]);

  return useMemo(() => ({
    kindFilter,
    setKindFilter,
    selectedFolderId,
    selectedPromptId,
    searchTerm,
    setSearchTerm,
    activeTag,
    selectedSpecialView,
    isTemplateManagerOpen,
    isPromptGeneratorOpen,
    selectedPromptIds,
    handleSelectPrompt,
    toggleSelectPrompt,
    clearPromptSelection,
    setSelectedPromptIds,
    handleSelectFolder,
    handleSelectTag,
    handleShowRecent,
    handleShowFavorites,
    handleShowTrash,
    handleOpenTemplateManager,
    handleOpenPromptGenerator,
    setSelectedPromptId,
    setSelectedFolderId,
    setSelectedSpecialView,
    setActiveTag,
    setIsTemplateManagerOpen,
    setIsPromptGeneratorOpen,
  }), [
    kindFilter,
    selectedFolderId,
    selectedPromptId,
    searchTerm,
    activeTag,
    selectedSpecialView,
    isTemplateManagerOpen,
    isPromptGeneratorOpen,
    selectedPromptIds,
    handleSelectPrompt,
    toggleSelectPrompt,
    clearPromptSelection,
    handleSelectFolder,
    handleSelectTag,
    handleShowRecent,
    handleShowFavorites,
    handleShowTrash,
    handleOpenTemplateManager,
    handleOpenPromptGenerator,
  ]);
};

export default useFolderSelection;
